/**
 * The writes a device kept while it had no network.
 *
 * Three rules decide everything in this file.
 *
 * **Only what the server said may be queued.** Each operation declares
 * `offline: 'queue'` on its route, and that declaration travels through the
 * published contract into the generated metadata this module reads. A list
 * maintained here instead would drift the first time somebody added a route,
 * and the drift would be invisible: a write that should have been kept would
 * be lost, or one that should have been refused would be replayed days late.
 *
 * **A queued write is never called saved.** The caller is told, in a distinct
 * error, that the work is on this device and not in the workspace. A client
 * that returned success would be lying about the one thing the person needs to
 * know before they close the laptop.
 *
 * **The queue id is the idempotency key.** One uuid, minted when the write is
 * kept and sent on every attempt, so a replay that arrives twice - because the
 * acknowledgement was lost, or two tabs woke together - is applied once by the
 * server. Without it, "offline capture" would mean duplicate records found by
 * whoever cleans the data six weeks later.
 */

import { OPERATIONS } from './generated/meta.js';
import type { RequestSpec } from './runtime.js';

/**
 * How much unsent work a device will hold, and for how long (F-139).
 *
 * **A cap, because the alternative is a phone that stops working.** IndexedDB
 * is large but not infinite, and the failure when it fills is a thrown quota
 * error in the middle of somebody's save - the worst possible moment to
 * discover a storage decision. Five hundred writes is more than a week in the
 * field and small enough to replay in one go.
 *
 * **An age limit, because the server's ledger is prunable.** `client_write`
 * holds the answer to each idempotency key, and its own comment says it is
 * safe to prune "once no device can still be holding the key". That sentence
 * needs a number behind it or the pruning is a guess: a write replayed after
 * its key was forgotten would be applied a second time, which is the exact
 * duplicate the key existed to prevent. Fourteen days is the promise.
 */
export const MAX_QUEUED_WRITES = 500;
export const MAX_QUEUED_AGE_DAYS = 14;

/** True when this write is older than the server can be relied on to remember. */
export function tooOldToReplay(write: QueuedWrite, now: Date = new Date()): boolean {
  const age = now.getTime() - Date.parse(write.queuedAt);
  return Number.isFinite(age) && age > MAX_QUEUED_AGE_DAYS * 86_400_000;
}

const DB_NAME = 'whinerp-offline';
const DB_VERSION = 1;
const STORE = 'writes';

export type QueuedState =
  /** Waiting for a network. Nothing has been attempted yet, or the last attempt found none. */
  | 'waiting'
  /** Sent, and the server answered with something only a person can settle. */
  | 'needs-you'
  /** Sent and refused for a reason replaying will not fix. */
  | 'refused';

export interface QueuedWrite {
  /** Also the idempotency key. One uuid for the life of this write. */
  id: string;
  queuedAt: string;
  operationId: string;
  spec: RequestSpec;
  /**
   * What the person did, in their words, written when the write was kept.
   *
   * Composed at the call site rather than derived here: "Priya Raman" means
   * something to the person reading a queue three hours later, and
   * "PATCH /records/contact/9f2c..." does not.
   */
  label: string;
  state: QueuedState;
  attempts: number;
  /** The server's answer to the last attempt, when there was one. */
  response: unknown;
  /** Why it is refused, in the server's words. Null unless state is 'refused'. */
  refusal: string | null;
}

/** True when the published contract says this operation may be kept and replayed. */
export function isQueueable(operationId: string): boolean {
  return OPERATIONS.some((op) => op.operationId === operationId && op.offline === 'queue');
}

/** Every operation the server will accept from a queue. Used by tests and the queue panel. */
export function queueableOperations(): string[] {
  return OPERATIONS.filter((op) => op.offline === 'queue').map((op) => op.operationId);
}

/**
 * What the server said needs a person.
 *
 * One shape, not a list of operations. An answer carrying a non-empty
 * `conflicts` array is one the server accepted as a question and declined to
 * settle - it applied the fields nobody disputed and handed back the ones that
 * moved, with both values. Deciding those is the person's, and a queue that
 * chose for them would be the silent overwrite the whole design exists to
 * prevent.
 */
export function needsAPerson(response: unknown): boolean {
  if (!response || typeof response !== 'object') return false;
  const conflicts = (response as { conflicts?: unknown }).conflicts;
  return Array.isArray(conflicts) && conflicts.length > 0;
}

export interface OfflineStore {
  put(write: QueuedWrite): Promise<void>;
  all(): Promise<QueuedWrite[]>;
  get(id: string): Promise<QueuedWrite | null>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;
}

/** Resolves when the request settles, rejecting with a real Error either way. */
function promise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('The offline store refused a request.'));
  });
}

/**
 * The store, in IndexedDB.
 *
 * IndexedDB rather than localStorage for one reason that matters: localStorage
 * is synchronous and capped at a few megabytes shared with everything else on
 * the origin, and a person who spends a day in the field writing notes will
 * reach that cap. The failure when they do is a thrown quota error in the
 * middle of a save, which is the worst possible moment to discover a storage
 * decision.
 */
export function indexedDbStore(factory: IDBFactory = globalThis.indexedDB): OfflineStore {
  let open: Promise<IDBDatabase> | null = null;

  const db = async (): Promise<IDBDatabase> => {
    open ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = factory.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STORE)) {
          database.createObjectStore(STORE, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('The offline store would not open.'));
    });
    return open;
  };

  const tx = async <T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => Promise<T>): Promise<T> => {
    const database = await db();
    const transaction = database.transaction(STORE, mode);
    const result = await fn(transaction.objectStore(STORE));
    // Waiting for the transaction, not only for the request. A put that has
    // "succeeded" inside a transaction that then aborts has not been written,
    // and the caller would have been told their note was safe.
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('The offline store aborted.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('The offline store aborted.'));
    });
    return result;
  };

  return {
    put: (write) => tx('readwrite', async (store) => void (await promise(store.put(write)))),
    remove: (id) => tx('readwrite', async (store) => void (await promise(store.delete(id)))),
    clear: () => tx('readwrite', async (store) => void (await promise(store.clear()))),
    get: (id) =>
      tx('readonly', async (store) => ((await promise(store.get(id))) as QueuedWrite | undefined) ?? null),
    all: () =>
      tx('readonly', async (store) => {
        const rows = ((await promise(store.getAll())) ?? []) as QueuedWrite[];
        // Oldest first, always. A queue replayed out of order can apply an
        // edit before the creation it depends on, and the person sees a
        // refusal for a record they are looking at.
        return rows.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt) || a.id.localeCompare(b.id));
      }),
  };
}
